import type { AttachmentAdapter, CompleteAttachment, PendingAttachment } from "@assistant-ui/react";

/**
 * Composer attachments (ADMIN-212). They are sent, and saved with the message in the thread's
 * Durable Object, as `data:` URLs in file parts, so they come back on reload. An object's SQLite
 * row holds at most 2 MB, so a file over `MAX_ATTACHMENT_BYTES` is refused and a large photo is
 * scaled down first. Text files are typed `text/plain` when the browser can't tell, so the API
 * hands them to the model as text.
 */

/** @public exported for its test */
export const MAX_ATTACHMENT_BYTES = 1_000_000;
/** A photo is scaled to fit this side, and re-encoded, when it's larger or too heavy. */
const MAX_IMAGE_SIDE = 2048;

const ATTACHMENT_ACCEPT =
  "image/*,application/pdf,text/*,application/json,.md,.markdown,.txt,.csv,.tsv,.json,.yaml,.yml,.xml,.html,.css,.js,.jsx,.ts,.tsx,.py,.rb,.go,.rs,.java,.c,.h,.cpp,.sh,.sql,.toml,.ini,.log";

const TEXT_EXTENSIONS = new Set(
  ATTACHMENT_ACCEPT.split(",")
    .filter((a) => a.startsWith("."))
    .map((a) => a.slice(1)),
);

/**
 * The media type to send a file as: the browser's, or text/plain for a text file it can't type.
 *
 * @public exported for its test
 */
export function attachmentMediaType(file: { name: string; type: string }): string {
  if (file.type && file.type !== "application/octet-stream") return file.type;
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  return TEXT_EXTENSIONS.has(ext) ? "text/plain" : file.type || "application/octet-stream";
}

function formatBytes(bytes: number): string {
  if (bytes < 1000) return `${bytes} B`;
  if (bytes < 1_000_000) return `${Math.round(bytes / 1000)} KB`;
  return `${(bytes / 1_000_000).toFixed(1)} MB`;
}

/**
 * Why a file can't be attached, or undefined when it can.
 *
 * @public exported for its test
 */
export function attachmentProblem(file: { name: string; size: number }): string | undefined {
  if (file.size > MAX_ATTACHMENT_BYTES) {
    return `${file.name} is ${formatBytes(file.size)}; attachments can be up to ${formatBytes(MAX_ATTACHMENT_BYTES)}.`;
  }
  return undefined;
}

/**
 * The size an image is drawn at to fit `MAX_IMAGE_SIDE`, keeping its shape.
 *
 * @public exported for its test
 */
export function fitImage(width: number, height: number, maxSide = MAX_IMAGE_SIDE) {
  const scale = Math.min(1, maxSide / Math.max(width, height));
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

/** A photo that's too big or too heavy, scaled down and saved as JPEG; anything else as it is. */
async function shrinkImage(file: File): Promise<File> {
  if (typeof createImageBitmap !== "function" || typeof OffscreenCanvas === "undefined") {
    return file;
  }
  if (file.type === "image/gif" || file.type === "image/svg+xml") return file;
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file;
  }
  const size = fitImage(bitmap.width, bitmap.height);
  const tooBig = size.width < bitmap.width;
  if (!tooBig && file.size <= MAX_ATTACHMENT_BYTES / 2) {
    bitmap.close();
    return file;
  }
  const canvas = new OffscreenCanvas(size.width, size.height);
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    bitmap.close();
    return file;
  }
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, size.width, size.height);
  ctx.drawImage(bitmap, 0, 0, size.width, size.height);
  bitmap.close();
  const blob = await canvas.convertToBlob({ type: "image/jpeg", quality: 0.85 });
  if (blob.size >= file.size) return file;
  const name = file.name.replace(/\.[^.]+$/, "") || "image";
  return new File([blob], `${name}.jpg`, { type: "image/jpeg" });
}

function dataUrl(file: File, signal?: AbortSignal): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    signal?.addEventListener("abort", () => reader.abort(), { once: true });
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.onabort = () => reject(new DOMException("Aborted", "AbortError"));
    reader.readAsDataURL(file);
  });
}

export const chatAttachmentAdapter: AttachmentAdapter = {
  accept: ATTACHMENT_ACCEPT,
  async add({ file }): Promise<PendingAttachment> {
    const type = attachmentMediaType(file);
    let prepared = type === file.type ? file : new File([file], file.name, { type });
    if (type.startsWith("image/")) prepared = await shrinkImage(prepared);
    const problem = attachmentProblem(prepared);
    if (problem) throw new Error(problem);
    return {
      id: crypto.randomUUID(),
      type: type.startsWith("image/") ? "image" : type.startsWith("text/") ? "document" : "file",
      name: prepared.name,
      file: prepared,
      contentType: prepared.type,
      content: [],
      status: { type: "requires-action", reason: "composer-send" },
    };
  },
  async send(attachment, options): Promise<CompleteAttachment> {
    return {
      ...attachment,
      status: { type: "complete" },
      content: [
        {
          type: "file",
          mimeType: attachment.contentType ?? attachment.file.type,
          filename: attachment.name,
          data: await dataUrl(attachment.file, options?.signal),
        },
      ],
    };
  },
  async remove() {},
};
